import type { TranslationKey } from "@/src/i18n";
import type { TerrainPatchType } from "@/src/types/map";

/**
 * Terrain patches are painted regions rather than point objects: a polygon plus
 * a fill treatment. They render above the floor fill and below the grid, walls
 * and objects, and are clipped to the floor so a patch cannot bleed outside the
 * room it belongs to. See DECISIONS.md D-021.
 */
export interface TerrainPatchDefinition {
  id: TerrainPatchType;
  /** Base wash colour, drawn semi-transparent over the floor. */
  fill: string;
  /** Line colour for the pattern drawn on top of the wash. */
  ink: string;
  /** Which pattern the renderer draws. */
  pattern: "stipple" | "wave" | "hatch";
  /** Pattern density; higher means more marks per cell. */
  density: number;
  labelKey: TranslationKey;
}

export const TERRAIN_PATCH_CATALOG: TerrainPatchDefinition[] = [
  { id: "rubble", fill: "rgba(126, 110, 92, 0.34)", ink: "#5e5142", pattern: "stipple", density: 5,
    labelKey: "patch.rubble" },
  { id: "shallow_water", fill: "rgba(116, 169, 191, 0.32)", ink: "#3f7d96", pattern: "wave", density: 3,
    labelKey: "patch.shallow_water" },
  { id: "deep_water", fill: "rgba(60, 116, 148, 0.52)", ink: "#2b5f7c", pattern: "wave", density: 5,
    labelKey: "patch.deep_water" },
  { id: "chasm", fill: "rgba(28, 24, 21, 0.62)", ink: "#141110", pattern: "hatch", density: 6,
    labelKey: "patch.chasm" },
];

export const DEFAULT_PATCH_TYPE: TerrainPatchType = "rubble";

const BY_ID = new Map(TERRAIN_PATCH_CATALOG.map((entry) => [entry.id, entry]));

export const getTerrainPatch = (type: TerrainPatchType | undefined): TerrainPatchDefinition =>
  (type ? BY_ID.get(type) : undefined) ?? TERRAIN_PATCH_CATALOG[0]!;

export const isTerrainPatchType = (value: unknown): value is TerrainPatchType =>
  typeof value === "string" && BY_ID.has(value as TerrainPatchType);

/** The shapes the patch tool can draw, mirroring the floor geometry tools. */
export const PATCH_SHAPES = ["rect", "ellipse", "polygon"] as const;
export type PatchShape = (typeof PATCH_SHAPES)[number];
